// rotate.js — OscillaScore Rotate Cue (sequence + continuous)

import { registerAnimation, autoStartAnimation } from "./animation.js";
import { isObjectTrig } from "./probeTriggers.js";
import { scheduleCueStart } from "./cueDispatcher.js";
import { readPivot, isOff, truthy } from "../parser/cueUtils.js";
import { mayOperate } from "./controlAccess.js";
import { createHitLabel, shouldCreateHitLabel, repositionHitLabelFast, repositionChildHitLabels, loadOscState, loadToggleState } from "./animOverlays.js";
import {
    applyPrestateBeforeStart,
    applyPrestateOnStart,
    ensureAnimWrapper,
    installOscToggleHandler,
    installToggleStatesHandler,
    armElement,
    needsArming,
    needsFadeIn,
    triggerFadeIn,
    isOscEnabled
} from "./animShared.js";

import { createOscOverlay } from "./osc.js";
import { makeValueGenerator, evalMaybeRandom, isRandomised } from "./audio/audioShared.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { publish, velocityChannels, bindParam } from '../control/paramBinding.js';
import { animeEase } from '../control/easing.js';
import { setupSpeedBinding, isSignalRef, isOscRef } from './cueParamBinding.js';
import { createAnimationLoop } from './cueAnimLoop.js';
import { secs, secPerBeat } from './beats.js';


// ============================================================
// OSC send helper for ROTATION
// - wraps angle into [0, 360)
// - also sends radians + 0–1 normalised
// - respects optional cfg.oscAddr (from oscaddr:"...")
// ============================================================
function sendOSCRotation(cfg, angle) {
    // If osc is not configured, skip
    if (!cfg.osc) return;
    // If oscToggle is enabled, only send when _oscEnabled is true (toggled on via double-click)
    if (cfg.oscToggle && !cfg._oscEnabled) return;

    const raw = Number(angle);

    // Safety — avoid NaN propagation
    const safe = Number.isFinite(raw) ? raw : 0;

    // wrap into [0,360)
    const deg = ((safe % 360) + 360) % 360;
    const rad = deg * (Math.PI / 180);
    const norm = deg / 360;

    const addr =
        cfg.oscaddr ??
        cfg.oscAddr ??
        cfg.addr ??
        null;

    const payload = {
        type: "osc_rotate",
        uid: cfg.uid,
        deg,
        rad,
        norm,
        timestamp: Date.now()
    };

    if (addr) payload.addr = addr;

    sendOSC(payload);
}

// ============================================================
// Utilities: pivot + current angle
// ============================================================
/**
 * pivot:<id> — turn about another element, wherever it is now.
 *
 * A pair of numbers fixes the centre for good, which is no use when the
 * centre itself moves: a precessing sky turns about a pole that travels from
 * one star to another over the piece, so a fixed pivot leaves it circling the
 * star it started on and the new trails come out centred on the old pole.
 * Named instead, the centre is read from that element every few frames and
 * the rotation follows it.
 */
function pivotTargetOf(value) {
    if (value == null || readPivot(value)) return null;      // a literal pair
    const id = String(value).trim().replace(/^["'`]+|["'`]+$/g, "");
    if (!id || /[[\],\s]/.test(id)) return null;
    return document.getElementById(id)
        || document.querySelector(`[data-oscilla*="uid:${id}"]`)
        || null;
}

/**
 * Put the wrapper's transform-origin on the target's centre.
 *
 * transform-origin is read in the wrapper's own pre-transform space, which is
 * its parent's user space — so the parent's screen matrix is what converts,
 * never the wrapper's own (that one already carries the rotation).
 */
function applyTrackedPivot(wrapper, target) {
    // Measured through getBBox + getScreenCTM, not getBoundingClientRect: a
    // marker for a pole is usually invisible (visibility:hidden), and Chrome
    // gives those an empty client rect — while the matrix and the box, which
    // is what actually says where it is, are both still there.
    //
    // Which frame to measure in depends on where the marker sits. One INSIDE
    // the turning group is carried by the very rotation it is meant to centre,
    // so measuring it against the group's parent has the pivot chase its own
    // turn, round and round. The group's own matrix carries that rotation too
    // and cancels it, leaving the marker's place in the sky — which only
    // changes when the marker itself travels, which is the point.
    const inside = wrapper?.contains?.(target);
    const parentM = (inside ? wrapper : wrapper?.parentElement)?.getScreenCTM?.();
    const targetM = target?.getScreenCTM?.();
    if (!parentM || !targetM) return false;
    let bb, inv;
    try { bb = target.getBBox(); inv = parentM.inverse(); } catch { return false; }
    if (!bb) return false;
    const cx = bb.x + bb.width / 2, cy = bb.y + bb.height / 2;
    const sx = targetM.a * cx + targetM.c * cy + targetM.e;      // its centre, on screen
    const sy = targetM.b * cx + targetM.d * cy + targetM.f;
    wrapper.style.transformOrigin =
        `${sx * inv.a + sy * inv.c + inv.e}px ${sx * inv.b + sy * inv.d + inv.f}px`;
    return true;
}

/**
 * The pivot this run asks for: a named element to follow, or a fixed point.
 *
 * A followed pivot is re-read from the animation's own frame callback (see
 * onUpdate), not from a loop of its own — one more loop is one more thing to
 * outlive, or fail to outlive, the animation it belongs to.
 */
function setPivot(node, cfg) {
    const target = pivotTargetOf(cfg?.pivot);
    cfg._pivotTarget = target || null;
    if (target) { applyTrackedPivot(node, target); return; }
    applySvgPivot(node, cfg?.pivot);
}

function applySvgPivot(el, pivot = null) {
    // A fixed pivot in the element's own units, from the cue — rotate(pivot:[x, y])
    // — or from a data-pivot="x,y" attribute, which scores carry. Either needs no measuring, so it holds for an element that is
    // hidden (display:none has no box) and for a pivot outside the element: a
    // label turned back about its star, inside a sky turning the other way,
    // rides with the star upright.
    //
    // The cue wins over the attribute, so a pivot can be changed in the DSL
    // editor without hunting through the SVG for one set years ago.
    const fromCue = readPivot(pivot);
    if (fromCue) {
        el.style.transformOrigin = `${fromCue.x}px ${fromCue.y}px`;
        return;
    }
    // The attribute is authored on the element; the cue animates a wrapper it
    // made INSIDE or around that element, which carries no attribute of its
    // own — so look up to the host, or the score turns about its bounding box
    // instead of its point.
    const host = el.closest?.("[data-pivot]") || el;
    const attr = readPivot(host.getAttribute?.("data-pivot"));
    const fromAttr = attr;
    if (fromAttr) {
        el.style.transformOrigin = `${fromAttr.x}px ${fromAttr.y}px`;
        return;
    }
    if (!el.getBBox) return;
    const bb = el.getBBox();
    // A straight line has no height (or no width) but still a middle: only
    // a box with no size at all has nothing to turn about.
    if (!bb || (bb.width === 0 && bb.height === 0)) return;

    // Calculate bounding box center
    const bboxCx = bb.x + bb.width / 2;
    const bboxCy = bb.y + bb.height / 2;

    // For elements centered at origin (within tolerance), use exact (0,0)
    // This fixes rotation drift for odd-sided polygons where bbox center
    // differs from geometric center
    const tolerance = 50; // pixels
    const useOrigin = Math.abs(bboxCx) < tolerance && Math.abs(bboxCy) < tolerance;

    const cx = useOrigin ? 0 : bboxCx;
    const cy = useOrigin ? 0 : bboxCy;

    el.style.transformOrigin = `${cx}px ${cy}px`;
}

/**
 * axis: -- what the turn is about. z (the default) turns the object in the
 * page; y turns it front to back about the vertical (a card flipping), x
 * top over bottom about the horizontal. Inside an SVG the browser flattens
 * a 3D turn: no perspective, the object narrows to its edge and comes back
 * mirrored. back:hide hides it while it faces away (an SVG has no back).
 */
const AXES = { x: "rotateX", y: "rotateY", z: "rotate" };
function turnAxis(cfg) {
    const raw = cfg?._axisNow ?? (typeof cfg?.axis === "string" ? cfg.axis : "z");
    const a = String(raw).toLowerCase();
    return AXES[a] ? a : "z";
}

/**
 * axis:Pchoose(x, y, z) -- a pattern draws the next axis at each step of a
 * sequence, each value a following rotate takes, each turn of a spin.
 */
function nextAxis(cfg) {
    const a = cfg?.axis;
    if (!a || typeof a !== "object" || a.type !== "pattern") return;
    if (!cfg._axisGen) cfg._axisGen = makeValueGenerator(a);
    let v = cfg._axisGen?.next?.();
    if (v == null) { cfg._axisGen = makeValueGenerator(a); v = cfg._axisGen?.next?.(); }
    if (v != null) cfg._axisNow = String(v).toLowerCase();
}

/** Turn node to deg about cfg's axis (and hide its back, if asked). */
function applyTurn(node, cfg, deg) {
    const axis = turnAxis(cfg);
    node.style.transform = `${AXES[axis]}(${deg}deg)`;
    if (axis !== "z" && String(cfg?.back ?? "").toLowerCase() === "hide") {
        node.style.visibility = Math.cos(deg * Math.PI / 180) < -1e-6 ? "hidden" : "";
    }
}

function getCurrentAngle(el, fallback = 0) {
    const t = el.style.transform || "";
    const m = t.match(/rotate[XYZ]?\(\s*([-\d.+eE]+)deg/);
    if (!m) return fallback;
    const v = parseFloat(m[1]);
    return Number.isFinite(v) ? v : fallback;
}


/**
 * sin / cos of the angle, as 0..1 like every other channel follow() reads.
 * norm is a sawtooth -- a spin snaps from 1 back to 0 each turn -- so
 * follow(x.norm, -1, 1) pans left to right and jumps back; follow(x.sin, -1, 1)
 * swings smoothly, once round per turn.
 */
function waveChannels(deg) {
  const r = deg * (Math.PI / 180);
  return { sin: (1 + Math.sin(r)) / 2, cos: (1 + Math.cos(r)) / 2 };
}

/**
 * Whatever rotate runs on this element gives way to a new one: a spin's loop
 * is stopped, a sequence's step chain halted (its tween and its hold), a
 * follow let go. They all turn the same wrapper, so two left running fight
 * over it frame by frame.
 */
function stopRotateOn(el) {
    const prev = el._oscillaRotateAnim;
    if (prev?.stop) prev.stop();        // a continuous loop
    else clearTimeout(prev);            // a sequence's hold (its tween: below)
    el._oscillaRotateAnim = null;
    el._oscillaRotateSeq?.stop();
    el._oscillaRotateSeq = null;
    el._oscillaRotateFollow?.();
}

// ======================================================================
//  ROTATION SEQUENCE ENGINE (sequence / patterns / lists)
//  handleRotateSequence(el, cfg)
// ======================================================================
export function handleRotateSequence(el, cfg) {
    stopRotateOn(el);
    cfg._stopped = false;               // a fresh run un-retires this cfg

    const astArgs = cfg.astArgs || [];
    const k = secPerBeat();               // dur: and hold: are beats

    // Value source
    let values = cfg.values || null;      // literal list
    let pattern = cfg.pattern || null;    // pattern object

    // Duration
    let dur = 1;
    let durGen = null;
    let durBinding = null;

    // Behaviour
    let mode = "loop";                    // loop | once | alternate
    let pauseOnExit = true;
    let interp = "smooth";                // smooth | step
    let ease = "linear";
    let hold = null;
    let oscMode = 0;                      // 0 off, 1 continuous, 2 per-step

    // Pattern generator
    let valueGen = null;

    // ------------------------------------------------------------
    // Parse AST args
    // ------------------------------------------------------------
    for (const arg of astArgs) {
        const key = arg.key || arg.type;
        const val = arg.value;

        switch (key) {
            case "values":
                if (val && val.type === "pattern") {
                    pattern = val;
                } else if (Array.isArray(val)) {
                    values = val.slice();
                }
                break;

            case "dur": {
                const d = secs(val, k);
                if (d && d.type === "pattern") {
                    durGen = makeValueGenerator(d);
                } else if (Array.isArray(d)) {
                    durGen = makeValueGenerator({ values: d });
                } else if (isSignalRef(d)) {
                    // Signal binding for dynamic dur
                    durBinding = d;
                    dur = d.range?.[0] ?? 1;  // use min as default
                    cfg.dur = dur;
                } else {
                    dur = Number(d) || 1;
                    cfg.dur = dur;  // Store for network sync
                }
                break;
            }

            case "mode":
                mode = String(val).trim().toLowerCase();
                break;

            case "pauseOnExit":
                pauseOnExit = Boolean(val);
                break;

            case "interp":
                interp = String(val).trim().toLowerCase();
                break;

            case "ease":
                ease = String(val).trim();
                break;

            case "hold":
                hold = Number(secs(val, k));
                break;

            case "osc":
                oscMode = Number(val) || 0;
                break;

            // explicit OSC address
            case "oscaddr":
            case "oscAddr":
                cfg.oscAddr = String(val).trim();
                break;

            default:
                break;
        }
    }

    // Assign oscMode to cfg.osc so sendOSCRotation can use it
    cfg.osc = oscMode;

    // Set up speed binding (handles both static and signal-bound dur)
    setupSpeedBinding(cfg, durBinding || dur, {
        kind: 'rotate',
        baseDur: dur,
        minDur: 0.25,
        maxDur: 120
    });

    // Determine value generator
    if (pattern) {
        valueGen = makeValueGenerator(pattern);
        values = null;
    }

    if (!values && !valueGen) {
        console.warn("[rotate] No value source (values:[] or pattern) for rotateSequence");
        return;
    }

    // Default hold behaviour
    if (interp === "smooth") {
        if (hold === null || Number.isNaN(hold)) {
            hold = dur * 0.25;
        }
    }
    if (interp === "step") {
        hold = 0;
    }

    const wrapper = ensureAnimWrapper(el, "rotate");
    setPivot(wrapper, cfg);

    // ----------------------------------------------
    // OVERLAY (anchors to rotating element, center)
    // only when OSC is actually enabled
    // ----------------------------------------------
    if (cfg._overlay) {
        cfg._overlay.destroy();
        cfg._overlay = null;
    }

    if (isOscEnabled(cfg, oscMode)) {
        cfg._overlay = createOscOverlay({
            anchorEl: wrapper,
            label: cfg.oscAddr || cfg.uid || "rotate",
            anchorMode: "center"
        });

        cfg._overlay.update("…");
        cfg._overlay.position();
    }

    // Literal list indexing
    const N = Array.isArray(values) ? values.length : Infinity;
    let index = 0;
    let direction = 1;

    // Driver angle - handle sync resume position. Turning there from where
    // it is (rotate(135, dur:2), _fromHere): nothing turned yet is 0 degrees --
    // falling back to the target read it as there already, and it never moved
    const fromHere = astArgs.some(a => a.type === "_fromHere");
    let initAngle = getCurrentAngle(wrapper, fromHere ? 0 : (values?.[0] ?? 0));
    if (cfg._dragResumeT != null) {
        initAngle = cfg._dragResumeT * 360;
        applyTurn(wrapper, cfg, initAngle);
        delete cfg._dragResumeT;
    }
    const norm = ((initAngle % 360) + 360) % 360;
    const driver = { a: norm };

    // ------------------------------------------------------------
    // Helpers
    // ------------------------------------------------------------
    function nextAngle() {
        if (valueGen) {
            const v = valueGen.next();
            if (v == null) return null;
            // rand()/irand() steps arrive as funcCall objects — evaluate here
            const n = Number(evalMaybeRandom(v));
            return Number.isFinite(n) ? n : null;
        }

        if (Array.isArray(values) && N > 0) {
            const n = Number(evalMaybeRandom(values[index % N]));
            return Number.isFinite(n) ? n : 0;
        }

        return driver.a;
    }

    function stepIndexAdvance() {
        if (valueGen) return; // pattern generator drives repetition

        const len = Array.isArray(values) ? values.length : 0;
        const first = Array.isArray(values) ? values[0] : null;
        const last = Array.isArray(values) ? values[len - 1] : null;
        const pingpong = len >= 2 && first === last;

        if (mode === "alternate" || pingpong) {
            index += direction;
            if (index >= N || index < 0) {
                direction *= -1;
                index += direction;
            }
        } else if (mode === "once") {
            index++;            // not round again: atEndOnce() ends it (it wrapped, and once never ended)
        } else {
            index = (index + 1) % N;
        }
    }

    function atEndOnce() {
        return (!valueGen && mode === "once" && index >= N);
    }

    // ------------------------------------------------------------
    // Observer pause/resume state
    // ------------------------------------------------------------
    let _paused = false;
    let _halted = false;            // stop(): a later rotate took the element over
    let _currentTween = null;
    let _holdStart = 0;
    let _holdRemaining = 0;
    let _skipRun = 0;               // equal targets in a row (runNext)

    // ------------------------------------------------------------
    // Main step engine
    // ------------------------------------------------------------
    function runNext() {
        if (_paused || _halted) return;

        nextAxis(cfg);                      // axis:Pchoose(...): one per step
        const targetRaw = nextAngle();
        if (targetRaw == null || atEndOnce()) {
            if (!pauseOnExit && Array.isArray(values) && values.length > 0) {
                applyTurn(wrapper, cfg, values[0]);
            }

            // cleanup overlay on finish
            if (cfg._overlay) {
                cfg._overlay.destroy();
                cfg._overlay = null;
            }

            el._oscillaRotateAnim = null;
            return;
        }

        let tgt = ((Number(targetRaw) % 360) + 360) % 360;

        // STEP MODE - apply speedMultiplier to duration
        const durRaw = durGen ? evalMaybeRandom(durGen.next()) : dur;
        const baseDur = Number(durRaw) || dur || 0.0001;
        const stepDur = baseDur / cfg.speedMultiplier;  // faster speedMultiplier = shorter duration

        if (interp === "step") {
            driver.a = tgt;
            applyTurn(wrapper, cfg, tgt);

            if (cfg._overlay) {
                cfg._overlay.update(`deg:${tgt.toFixed(1)}`);
            }

            if (isOscEnabled(cfg, oscMode)) {
                sendOSCRotation(cfg, Number(tgt) || 0);
            }

            stepIndexAdvance();
            _holdStart = performance.now();
            _holdRemaining = stepDur * 1000;  // stepDur already adjusted by speedMultiplier
            el._oscillaRotateAnim = setTimeout(
                () => {
                    _holdRemaining = 0;
                    requestAnimationFrame(runNext);
                },
                stepDur * 1000  // stepDur already adjusted by speedMultiplier
            );
            return;
        }

        // SMOOTH MODE — drift-compensated
        let current = getCurrentAngle(wrapper, driver.a);
        current = ((current % 360) + 360) % 360;
        driver.a = current;

        // Already there: on to the next value at once, but bounded. A
        // one-value or all-equal sequence would otherwise go round every
        // frame for ever; after a full pass of equal targets it holds for
        // a step instead (scale.js does the same).
        if (driver.a === tgt) {
            stepIndexAdvance();
            if (++_skipRun >= Math.max(2, Number.isFinite(N) ? N : 8)) {
                _skipRun = 0;
                _holdStart = performance.now();
                _holdRemaining = stepDur * 1000;
                el._oscillaRotateAnim = setTimeout(() => {
                    _holdRemaining = 0;
                    requestAnimationFrame(runNext);
                }, stepDur * 1000);
                return;
            }
            requestAnimationFrame(runNext);
            return;
        }
        _skipRun = 0;

        let delta = tgt - driver.a;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;

        const anim = anime.animate(driver, {
            a: driver.a + delta,
            duration: stepDur * 1000,
            ease: animeEase(ease),

            onUpdate: () => {
                repositionHitLabelFast(cfg.uid);
                repositionChildHitLabels(wrapper); // Update labels for child elements

                let a = ((driver.a % 360) + 360) % 360;
                const angleDeg = Number(a) || 0;

                // pivot:<id> — read where the named element has got to. Done
                // here rather than on a loop of its own, so following the
                // pivot lasts exactly as long as the rotation does.
                if (cfg._pivotTarget && ((cfg._pivotTick = (cfg._pivotTick || 0) + 1) & 3) === 0) {
                    applyTrackedPivot(wrapper, cfg._pivotTarget);
                }

                applyTurn(wrapper, cfg, a);

                if (cfg._overlay) {
                    cfg._overlay.update(`deg:${a.toFixed(1)}`);
                }

                // ========== CONTROL PLANE PUBLISH ==========
                const wrapped = ((angleDeg % 360) + 360) % 360;
                // Angular velocity in deg/s, tvel in turns/s.
                // driver.a is re-wrapped to 0-360 at each step, so accumulate the
                // shortest-arc difference instead of feeding the wrapped angle in.
                let arc = angleDeg - (cfg._velPrevA ?? angleDeg);
                if (arc > 180) arc -= 360; else if (arc < -180) arc += 360;
                cfg._velPrevA = angleDeg;
                cfg._velTurned = (cfg._velTurned ?? 0) + arc;
                const { vel, tvel } = velocityChannels("rotate", cfg.uid, cfg, cfg._velTurned, 0, 360);
                publish("rotate", cfg.uid, {
                    angle: wrapped,
                    rad: wrapped * (Math.PI / 180),
                    norm: wrapped / 360,
                    ...waveChannels(wrapped),
                    vel, tvel
                });
                // ============================================

                // Store for network sync
                cfg._currentAngle = wrapped;
                cfg._currentT = wrapped / 360;  // Normalized 0-1 for sync system

                if (isOscEnabled(cfg, oscMode)) {
                    sendOSCRotation(cfg, Number(a) || 0);
                }
            },

            onComplete: () => {
                if (isOscEnabled(cfg, oscMode)) {
                    const a = getCurrentAngle(wrapper, 0);
                    sendOSCRotation(cfg, Number(a) || 0);
                }

                stepIndexAdvance();

                if (hold > 0) {
                    const adjustedHold = hold / cfg.speedMultiplier;
                    _holdStart = performance.now();
                    _holdRemaining = adjustedHold * 1000;
                    el._oscillaRotateAnim = setTimeout(
                        () => {
                            _holdRemaining = 0;
                            requestAnimationFrame(runNext);
                        },
                        adjustedHold * 1000
                    );
                } else {
                    requestAnimationFrame(runNext);
                }
            }
        });

        el._oscillaRotateAnim = anim;
        _currentTween = anim;
    }

    requestAnimationFrame(runNext);

    // ---------------------------------------------------------------
    // Observer pause/resume wrapper
    // Lets the visibility observer halt the entire step chain,
    // not just the current anime tween.
    // ---------------------------------------------------------------
    cfg._anim = {
        pause() {
            _paused = true;
            // Cancel any pending hold/step timeout
            clearTimeout(el._oscillaRotateAnim);
            // Save remaining hold time so we can resume accurately
            if (_holdRemaining > 0) {
                _holdRemaining = Math.max(0, _holdRemaining - (performance.now() - _holdStart));
            }
            // Pause current tween if one is active
            if (_currentTween && typeof _currentTween.pause === 'function') {
                _currentTween.pause();
            }
        },
        stop() {
            this.pause();
            _halted = true;
            _holdRemaining = 0;
        },
        play() {
            if (cfg._stopped || _halted) return;   // retired by a reset — see trans.js _anim.stop()
            if (!_paused) return;
            _paused = false;

            // Resuming from a hold timeout — re-schedule remaining time
            if (_holdRemaining > 0) {
                _holdStart = performance.now();
                el._oscillaRotateAnim = setTimeout(() => {
                    _holdRemaining = 0;
                    requestAnimationFrame(runNext);
                }, _holdRemaining);
            }
            // Resuming mid-tween — let anime continue (complete callback chains)
            else if (_currentTween && typeof _currentTween.play === 'function'
                     && !_currentTween.completed) {
                _currentTween.play();
            }
            // Otherwise restart the chain
            else {
                requestAnimationFrame(runNext);
            }
        },
        // OSC /oscilla/<uid>/angle: there at once. A turn under way is cut
        // and the sequence turns on from here to the value it was heading
        // for; a hold (interp:step, hold:) runs out as before.
        setAngle(deg) {
            const a = ((deg % 360) + 360) % 360;
            const turning = _holdRemaining <= 0 && _currentTween && !_currentTween.completed;
            if (turning) {
                _currentTween.pause();
                _currentTween = null;
            }
            driver.a = a;
            applyTurn(wrapper, cfg, a);
            cfg._currentAngle = a;
            cfg._currentT = a / 360;
            if (turning && !_paused && !_halted) requestAnimationFrame(runNext);
        }
    };
    el._oscillaRotateSeq = cfg._anim;   // for stopRotateOn
}


// ============================================================
// Following: the angle follows a signal -- rotate(follow(bass.t, 0, 360)),
// or values:follow(...), as a scale's size does. Each new value sets the
// angle at once; glide: (beats) eases to it instead.
// ============================================================
export function handleRotateSignalBound(el, cfg, ref) {
    cfg._stopped = false;
    // a rotate already on this element -- a spin, a sequence, an earlier
    // follow -- gives way: they share the wrapper, and a spin left running
    // overwrote the followed angle every frame
    stopRotateOn(el);
    const wrapper = ensureAnimWrapper(el, "rotate");
    setPivot(wrapper, cfg);

    const glideArg = (cfg.astArgs || []).find(a => (a.key || a.type) === "glide")?.value;
    const glide = Math.max(0, Number(secs(glideArg)) || 0);
    let target = getCurrentAngle(wrapper, 0);
    let shown = target;
    let paused = false;
    let raf = 0;
    let last = performance.now();

    const show = (deg) => {
        applyTurn(wrapper, cfg, deg);
        repositionHitLabelFast(cfg.uid);
        const wrapped = ((deg % 360) + 360) % 360;
        cfg._currentAngle = wrapped;
        cfg._currentT = wrapped / 360;
        publish("rotate", cfg.uid, {
            angle: wrapped, rad: wrapped * (Math.PI / 180), norm: wrapped / 360, ...waveChannels(wrapped)
        });
    };

    // with a glide: ease towards the target, frame by frame
    const frame = (now) => {
        raf = 0;
        if (paused || cfg._stopped) return;
        const dt = Math.min(0.1, (now - last) / 1000);
        last = now;
        shown += (target - shown) * (1 - Math.exp(-dt / Math.max(0.005, glide / 3)));
        show(shown);
        if (Math.abs(target - shown) > 0.05) raf = requestAnimationFrame(frame);
    };

    const binding = bindParam(ref, (deg) => {
        const v = Number(deg);
        if (!Number.isFinite(v)) return;
        target = v;
        nextAxis(cfg);                      // axis:Pchoose(...): one per value
        if (paused || cfg._stopped) return;
        if (glide > 0) { if (!raf) { last = performance.now(); raf = requestAnimationFrame(frame); } }
        else { shown = v; show(v); }
    }, {
        owner: `rotate:${cfg.uid || "anon"}:angle`,
        min: ref.range?.[0] ?? 0,
        max: ref.range?.[1] ?? 360,
        default: ref.default ?? ref.range?.[0] ?? 0,
    });

    // what a later rotate on this element calls to take over
    el._oscillaRotateFollow = () => {
        binding?.unbind?.();
        cfg._stopped = true;
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        el._oscillaRotateFollow = null;
    };

    cfg._anim = {
        pause() { paused = true; if (raf) cancelAnimationFrame(raf); raf = 0; },
        play() {
            if (cfg._stopped || !paused) return;
            paused = false;
            if (glide > 0) { last = performance.now(); raf = requestAnimationFrame(frame); }
            else { shown = target; show(target); }
        },
    };
}


// ============================================================
// Continuous fallback rotation (no values:[] provided)
// Uses shared animation loop helper for dynamic speed control
// ============================================================
export function handleRotateContinuous(el, cfg) {
    stopRotateOn(el);                   // a spin, a sequence or a follow on it gives way
    cfg._stopped = false;               // a fresh run un-retires this cfg
    nextAxis(cfg);                      // the first turn's axis, from a pattern

    const astArgs = cfg.astArgs || [];

    let dir = 1;
    let dirRaw = null;      // the authored expression, kept for re-rolling
    let durRaw = null;
    let durValue = 2;
    let durBinding = null;
    let loop = 0;
    let oscMode = 0;
    let bipolar = false;

    for (const arg of astArgs) {
        const key = arg.key || arg.type;
        const value = arg.value;

        if (key === "dir") { dirRaw = value; dir = Number(evalMaybeRandom(value)) || 1; }
        if (key === "bipolar") bipolar = (Number(value) === 1 || value === true || value === "1");
        if (key === "dur") {
            // beats -> seconds once here; durRaw keeps the seconds form, so
            // a re-roll draws seconds too
            const d = secs(value);
            if (isSignalRef(d)) {
                durBinding = d;
                durValue = d.range?.[0] ?? 2;
            } else {
                // rand()/rnd() arrive as funcCall objects or literal text;
                // Number() alone gives NaN and every member would fall back
                // to the same 2 s turn.
                durRaw = d;
                durValue = Number(evalMaybeRandom(d)) || 2;
            }
            cfg.dur = durValue;
        }
        if (key === "loop") loop = Number(value);
        if (key === "pivot") cfg.pivot = value;
        if (key === "osc") oscMode = Number(value) || 0;
        if (key === "oscaddr") cfg.oscAddr = String(value).trim();
    }

    cfg.osc = oscMode;

    const speedBinding = setupSpeedBinding(cfg, durBinding || durValue, {
        kind: 'rotate',
        baseDur: durValue,
        minDur: 0.5,
        maxDur: 120,
        bipolar,
    });

    const animEl = ensureAnimWrapper(el, "rotate");
    setPivot(animEl, cfg);

    if (cfg._overlay) {
        cfg._overlay.destroy();
        cfg._overlay = null;
    }

    if (isOscEnabled(cfg, oscMode)) {
        cfg._overlay = createOscOverlay({
            anchorEl: animEl,
            label: cfg.oscAddr || cfg.uid || "rotate",
            anchorMode: "center"
        });
        cfg._overlay.update("…");
        cfg._overlay.position();
    }

    // Handle sync resume position
    let currentAngle = getCurrentAngle(animEl, 0);
    if (cfg._dragResumeT != null) {
        currentAngle = cfg._dragResumeT * 360;
        applyTurn(animEl, cfg, currentAngle);
        delete cfg._dragResumeT;
    }

    // A rand()/Prand() on dir: or dur: means "pick again", not "pick once".
    // Both are read once at setup, so they are re-rolled at each loop, or a
    // member would spin the same way at the same speed for ever.
    const rerollsDir = isRandomised(dirRaw);
    const rerollsDur = isRandomised(durRaw);
    let degreesPerSecond = (dir * 360) / durValue;
    const reroll = () => {
        if (rerollsDir) dir = Number(evalMaybeRandom(dirRaw)) || 1;
        if (rerollsDur) durValue = Number(evalMaybeRandom(durRaw)) || durValue;
        degreesPerSecond = (dir * 360) / durValue;
    };

    // Create animation loop using shared helper
    const animLoop = createAnimationLoop({
        dur: durValue,
        loop,
        getSpeed: () => cfg.speedMultiplier ?? 1,
        onLoopComplete: () => { nextAxis(cfg); if (rerollsDir || rerollsDur) reroll(); },

        onTick: (phase, dt) => {
            const deltaAngle = degreesPerSecond * (cfg.speedMultiplier ?? 1) * dt;
            currentAngle += deltaAngle;
            cfg._velClock = (cfg._velClock ?? 0) + dt * 1000;

            const wrapped = ((currentAngle % 360) + 360) % 360;

            // pivot:<id> — read where the named element has got to, so a
            // rotation can turn about something that moves (a precessing pole
            // travelling from one star to the next). Every fourth frame is
            // plenty, and it costs nothing when the pivot is a fixed point.
            if (cfg._pivotTarget && ((cfg._pivotTick = (cfg._pivotTick || 0) + 1) & 3) === 0) {
                applyTrackedPivot(animEl, cfg._pivotTarget);
            }

            applyTurn(animEl, cfg, currentAngle);

            repositionHitLabelFast(cfg.uid);
            repositionChildHitLabels(animEl); // Update labels for child elements

            if (isOscEnabled(cfg, oscMode)) {
                sendOSCRotation(cfg, wrapped);
                if (cfg._overlay) cfg._overlay.update(`deg:${wrapped.toFixed(1)}`);
            }

            const { vel, tvel } = velocityChannels("rotate", cfg.uid, cfg, currentAngle, 0, 360, cfg._velClock);
            publish("rotate", cfg.uid, {
                angle: wrapped,
                rad: wrapped * (Math.PI / 180),
                norm: wrapped / 360,
                ...waveChannels(wrapped),
                vel, tvel
            });

            cfg._currentAngle = wrapped;
            cfg._currentT = wrapped / 360;
        },

        onComplete: () => {
            speedBinding.unbind();
        }
    });

    el._oscillaRotateAnim = animLoop;
    cfg._anim = animLoop;

    // Wrap setPhase to also update currentAngle for sync correction
    // The original setPhase only updates the internal loop phase, but rotate
    // tracks currentAngle separately, so we need to sync both
    const originalSetPhase = animLoop.setPhase.bind(animLoop);
    animLoop.setPhase = (p) => {
        originalSetPhase(p);
        // Convert normalized phase (0-1) to angle and update visual
        currentAngle = p * 360;
        applyTurn(animEl, cfg, currentAngle);
        cfg._currentAngle = p * 360;
        cfg._currentT = p;
    };

    animLoop.start();
}


// ============================================================================
// armClickToStart — trig:click for rotate, no hit label.
// Plain DOM click listener on the element itself; first click starts,
// subsequent clicks toggle pause/resume via the oscilla-hit armed handler.
// ============================================================================
function armClickToStart(el, startFn) {
    el.style.pointerEvents = "all";
    el.style.cursor = "pointer";
    const handler = (e) => {
        e.stopPropagation();
        if (!mayOperate(el)) return;          // allow:/lock: (controlAccess.js)
        el.removeEventListener("click", handler);
        el.style.cursor = "";
        startFn();
        // After starting, wire subsequent clicks to toggle via oscilla-hit
        el.addEventListener("click", (e2) => {
            e2.stopPropagation();
            if (!mayOperate(el)) return;
            el.dispatchEvent(new CustomEvent("oscilla-hit", { bubbles: true, detail: { kind: "rotate" } }));
        });
    };
    el.addEventListener("click", handler);
}

// ============================================================================
// ROTATE cue handler — supports tdelay + init(show|hide|ghost|fadein|armed)
// With three-phase armed: REGISTERED → ARMED → RUNNING
// ============================================================================
/**
 * rotate(45) -- to an angle, and stay: at once; rotate(45, dur:2) turning
 * there over 2 beats from where it is. Written as the sequence the rest of
 * this file plays: values:[45], once (a jump: interp:step).
 */
function fixedAngleArgs(args) {
    const isNum = v => typeof v === "number" || (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)));
    const at = args.findIndex(a => (a.key || a.type) === "values" && isNum(a.value));
    if (at < 0) return args;
    const has = k => args.some(a => (a.key || a.type) === k);
    const out = args.map((a, i) => (i === at ? { ...a, value: [Number(a.value)] } : a));
    if (!has("mode")) out.push({ type: "mode", value: "once" });
    if (!has("dur") && !has("interp")) out.push({ type: "interp", value: "step" });
    else out.push({ type: "_fromHere", value: true });     // from where it is, not set to it first
    return out;
}

export function handleRotateCue(el, astArgs, options = {}) {

    if (!el) return;
    astArgs = fixedAngleArgs(astArgs || []);

    const { fromCueTrigger = false } = options;

    // CHECK: playhead trigger for already-registered element?
    const existingCfg = el._oscillaCfg;

    if (fromCueTrigger && existingCfg && existingCfg._armed) {
        if (needsArming(existingCfg)) {
            armElement(el, existingCfg);
        }
        return;
    }

    // CHECK: playhead trigger for fadein element?
    if (fromCueTrigger && existingCfg && existingCfg._fadeIn) {
        if (needsFadeIn(existingCfg)) {
            triggerFadeIn(el, existingCfg);
        }
        return;
    }

    // ---------------------------------
    // Parse trig, uid, tdelay, init
    // ---------------------------------
    let trig = "auto";
    let uid = el.id || ("rotate_" + Math.random().toString(36).slice(2));
    let cfgStartDelay = 0;
    let init = "show";
    let sync = true;  // Network sync enabled by default
    let syncExplicit = false;
    let oscToggle = false;
    let oscAddr = null;
    let toggleStates = null;
    let toggleColors = null;
    let onToggle = null;

    for (const a of astArgs) {
        const key = a.key || a.type;
        const val = a.value;

        if (key === "trig") trig = String(val).toLowerCase();
        if (key === "uid") uid = String(val).trim();
        if (key === "tdelay") cfgStartDelay = Number(secs(val)) || 0;   // beats
        if (key === "sync") { syncExplicit = true; sync = !isOff(val); }
        if (key === "oscToggle") oscToggle = truthy(val);
        if (key === "oscAddr" || key === "oscaddr") oscAddr = String(val).trim();

        // Toggle states (decoupled from OSC)
        if (key === "toggleStates" || key === "togglestates") {
            if (typeof val === "string") {
                toggleStates = val.replace(/[\[\]]/g, "").split(",").map(v => v.trim()).filter(v => v);
            } else if (Array.isArray(val)) {
                toggleStates = val.map(String);
            }
        }
        if (key === "toggleColors" || key === "togglecolors") {
            if (typeof val === "string") {
                toggleColors = val.replace(/[\[\]]/g, "").split(",").map(v => v.trim()).filter(v => v);
            } else if (Array.isArray(val)) {
                toggleColors = val.map(String);
            }
        }
        if (key === "onToggle" || key === "ontoggle") {
            onToggle = String(val);
        }

        if (key === "init" && val != null) {
            init = val;
        }
    }

    // Random patterns / rand() draw per client by design: no drift correction
    // unless sync:1 is authored (see isRandomised).
    if (!syncExplicit && astArgs.some(a => isRandomised(a.value))) sync = false;
    const isScrollMode = window.mode === "scroll";
    // trig:<uid> waits for its source object (probeTriggers) even in page mode
    const shouldStartNow =
        fromCueTrigger || ((trig === "auto" || !isScrollMode) && !isObjectTrig(trig));

    // ----------------------------------------------------
    // BASE CFG (shared across modes)
    // ----------------------------------------------------
    const baseCfg = {
        uid,
        trig,
        // pivot:[x, y] — the point the element turns about. On the base config
        // so every mode (continuous, sequence, pattern) gets it, and read from
        // the args rather than a local so an unparsed value simply falls back
        // to the bounding-box centre.
        pivot: astArgs?.find?.(a => (a.key || a.type) === "pivot")?.value ?? null,
        // axis:x|y|z and back:hide (applyTurn), for every mode the same way
        axis: astArgs?.find?.(a => (a.key || a.type) === "axis")?.value ?? null,
        back: astArgs?.find?.(a => (a.key || a.type) === "back")?.value ?? null,
        start: cfgStartDelay,
        init,
        sync,
        oscToggle,
        oscAddr,
        toggleStates,
        toggleColors,
        onToggle,
        astArgs,
        fromCueTrigger,
        kind: "rotate",
        _anim: null
    };

    // Restore OSC toggle state from localStorage (legacy)
    if (oscToggle && !toggleStates && uid) {
        if (loadOscState(uid)?.oscMode) baseCfg._oscEnabled = true;
    }

    // Restore toggle states from localStorage
    if (toggleStates && uid) {
        const saved = loadToggleState(uid);
        if (saved) {
            baseCfg._toggleStateIndex = saved.stateIndex ?? 0;
            baseCfg._toggleColor = saved.color || null;
        }
        baseCfg._toggleStateIndex = baseCfg._toggleStateIndex ?? 0;
    }

    // ensure the element actually receives clicks
    el.style.pointerEvents = "all";
    el.parentNode?.appendChild(el);

    function makeRawStart(cfg, modeFn) {
        return () => {
            modeFn(el, cfg);
        };
    }

    function wrapStart(cfg, rawStartFn) {
        cfg._start = rawStartFn;
        cfg._applyPrestateOnStart = () => applyPrestateOnStart(el, cfg);

        return () => {
            if (cfg.start > 0) {
                scheduleCueStart(
                    cfg,
                    el,
                    () => {
                        if (cfg._armed && cfg._startBlocked) {
                            applyPrestateOnStart(el, cfg);
                            return;
                        }

                        applyPrestateOnStart(el, cfg);
                        rawStartFn();
                    },
                    cfg.uid
                );

            } else {
                if (cfg._armed && cfg._startBlocked) {
                    applyPrestateOnStart(el, cfg);
                    return;
                }
                applyPrestateOnStart(el, cfg);
                rawStartFn();
            }
        };
    }

    // ----------------------------------------------------
    // FOLLOWING: rotate(follow(bass.t, 0, 360)) -- the angle follows a signal
    // ----------------------------------------------------
    const followArg = astArgs.find(o => (o.key === "values" || o.type === "values")
        && (isSignalRef(o.value) || isOscRef(o.value)));
    if (followArg) {
        const prev = el._oscillaCfg;
        if (prev && prev.kind === "rotate") prev._stopped = true;   // not restarted by play
        const cfg = { ...baseCfg, mode: "signal-bound" };
        el._oscillaCfg = cfg;
        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyPrestateBeforeStart(el, cfg);

        const rawStart = makeRawStart(cfg, (e, c) => handleRotateSignalBound(e, c, followArg.value));
        const start = wrapStart(cfg, rawStart);
        registerAnimation(el, "rotate-signal", cfg, start);

        if (shouldCreateHitLabel(cfg)) {
            createHitLabel(el, "rotate", cfg.uid, {
                anchorMode: "pathStart", color: "cyan", sizeMode: "rotate40",
                oscToggle: cfg.oscToggle, toggleStates: cfg.toggleStates,
                toggleColors: cfg.toggleColors, onToggle: cfg.onToggle
            });
        }
        if (shouldStartNow) autoStartAnimation(cfg.uid, start, fromCueTrigger);
        else if (trig === "click") armClickToStart(el, start);
        return;
    }

    // ----------------------------------------------------
    // SEQUENCE MODE 1: Pattern sequence
    // ----------------------------------------------------
    const valuesArg = astArgs.find(o =>
        o.key === "values" || o.type === "values"
    );

    if (valuesArg && valuesArg.value?.type === "pattern") {
        const v = valuesArg.value;
        const cfg = {
            ...baseCfg,
            pattern: v,
            mode: "sequence-pattern"
        };

        el._oscillaCfg = cfg;

        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyPrestateBeforeStart(el, cfg);

        if (Array.isArray(v.values)) {
            const initWrapper = ensureAnimWrapper(el, "rotate");
            // Pivot FIRST — without a transform-origin the rotation is
            // about the viewBox center, swinging the element far away
            // (and the visibility observer may then pause it offscreen)
            setPivot(initWrapper, cfg);
            const angle = Number(evalMaybeRandom(v.values[0]));
            if (Number.isFinite(angle)) {
                applyTurn(initWrapper, cfg, angle);
            }
        }

        const rawStart = makeRawStart(cfg, handleRotateSequence);
        const start = wrapStart(cfg, rawStart);

        registerAnimation(el, "rotate-sequence", cfg, start);

        if (shouldCreateHitLabel(cfg)) {
            createHitLabel(el, "rotate", cfg.uid, {
                anchorMode: "pathStart",
                color: "cyan",
                sizeMode: "rotate40",
                oscToggle: cfg.oscToggle,
                toggleStates: cfg.toggleStates,
                toggleColors: cfg.toggleColors,
                onToggle: cfg.onToggle
            });
        }

        if (shouldStartNow) autoStartAnimation(cfg.uid, start, fromCueTrigger);
        else if (trig === "click") armClickToStart(el, start);
        return;
    }

    // ----------------------------------------------------
    // SEQUENCE MODE 2: Literal sequence
    // ----------------------------------------------------
    if (valuesArg && Array.isArray(valuesArg.value)) {
        const cfg = {
            ...baseCfg,
            values: valuesArg.value,
            mode: "sequence"
        };

        el._oscillaCfg = cfg;
        el.dataset.oscillaUid = cfg.uid;

        installOscToggleHandler(el, cfg);
        installToggleStatesHandler(el, cfg);
        applyPrestateBeforeStart(el, cfg);

        // set to its first angle -- unless it is to turn there (rotate(45, dur:2))
        if (!astArgs.some(a => a.type === "_fromHere")) {
            const initWrapper = ensureAnimWrapper(el, "rotate");
            const angle = cfg.values[0];
            applyTurn(initWrapper, cfg, angle);
        }

        const rawStart = makeRawStart(cfg, handleRotateSequence);
        const start = wrapStart(cfg, rawStart);

        registerAnimation(el, "rotate-sequence", cfg, start);

        if (shouldCreateHitLabel(cfg)) {
            createHitLabel(el, "rotate", cfg.uid, {
                anchorMode: "pathStart",
                color: "cyan",
                sizeMode: "rotate40",
                oscToggle: cfg.oscToggle,
                toggleStates: cfg.toggleStates,
                toggleColors: cfg.toggleColors,
                onToggle: cfg.onToggle
            });
        }

        if (shouldStartNow) autoStartAnimation(cfg.uid, start, fromCueTrigger);
        else if (trig === "click") armClickToStart(el, start);
        return;
    }

    // ----------------------------------------------------
    // CONTINUOUS MODE
    // ----------------------------------------------------
    const cfg = {
        ...baseCfg,
        mode: "continuous"
    };

    el._oscillaCfg = cfg;

    installOscToggleHandler(el, cfg);
    installToggleStatesHandler(el, cfg);
    applyPrestateBeforeStart(el, cfg);

    const rawStart = makeRawStart(cfg, handleRotateContinuous);
    const start = wrapStart(cfg, rawStart);

    registerAnimation(el, "rotate-fallback", cfg, start);

    if (shouldCreateHitLabel(cfg)) {
        createHitLabel(el, "rotate", cfg.uid, {
            anchorMode: "pathStart",
            color: "cyan",
            sizeMode: "rotate40",
            oscToggle: cfg.oscToggle,
            toggleStates: cfg.toggleStates,
            toggleColors: cfg.toggleColors,
            onToggle: cfg.onToggle
        });
    }

    if (shouldStartNow) autoStartAnimation(cfg.uid, start, fromCueTrigger);
    else if (trig === "click") armClickToStart(el, start);
}
